/**
 * What the five questions actually say about the student.
 *
 * WHY THIS EXISTS
 * The result screen is the whole payoff. A student who answers five
 * questions and gets back only "3 of 5 correct" has been handed a score,
 * which is the one thing they can already get from every other tool. The
 * promise made on the intro screen is "most tools stop at what you got
 * wrong, Maren is about why", and this is the module that has to pay it.
 *
 * WHAT COUNTS AS A PATTERN
 * The same reason named on two DIFFERENT questions. Two reasons on a
 * single question is one bad moment, not a habit, and calling it a
 * pattern would be the kind of overclaim that costs trust immediately:
 * a premed can tell the difference. Five questions cannot support more
 * than this, which is why the heading itself backs off when nothing
 * repeats rather than promising a pattern that is not there.
 */

import type { ErrorTag } from "./errorTags";
import { questionById, isShaky, type DiagnosticAnswer } from "./diagnosticQuestions";

/**
 * Sentence-fragment phrasings. ERROR_TAG_SHORT exists for chips and is
 * too clipped for prose ("POE" lowercases to "poe", which reads as a
 * typo), and ERROR_TAGS is a full label that will not sit inside a
 * sentence.
 */
export const REASON_PHRASE: Record<ErrorTag, string> = {
  conceptual_gap: "not knowing the concept",
  misread: "misreading the question",
  careless: "careless slips",
  timing: "rushing or running out of time",
  formula_recall: "blanking on the formula",
  process_of_elimination: "narrowing to two and picking wrong",
  trap_answer: "falling for the trap answer",
};

export interface ReasonTally {
  tag: ErrorTag;
  /** Distinct question ids. The count that decides a pattern. */
  questionIds: string[];
  subtopics: string[];
}

/** Comma list, capped. Safe only for items with no internal punctuation,
 *  which means reasons and resource names, never topic names. */
export function listOf(items: string[], max: number): string {
  const unique = [...new Set(items)];
  if (unique.length <= max) return unique.join(", ");
  return `${unique.slice(0, max).join(", ")}, plus ${unique.length - max} more`;
}

/**
 * Topic names carry their own commas ("Work, Energy & Power"), so a
 * comma-joined list of them reads as twice as many topics as it names.
 * Joining with "and" is unambiguous here because the canonical taxonomy
 * spells conjunctions as "&", so the word "and" only ever appears as the
 * separator this function inserted.
 */
export function joinTopics(items: string[], max = 2): string {
  const unique = [...new Set(items)];
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0];
  const head = unique.slice(0, max).join(" and ");
  const rest = unique.length - max;
  return rest > 0 ? `${head}, plus ${rest} more` : head;
}

/**
 * Tally reasons across the answers worth diagnosing, counting DISTINCT
 * questions rather than raw selections. Selecting two reasons on one
 * question must never look like the same reason twice.
 */
export function tallyReasons(answers: DiagnosticAnswer[]): ReasonTally[] {
  const byTag = new Map<ErrorTag, ReasonTally>();

  for (const a of answers.filter(isShaky)) {
    const subtopic = questionById(a.id)?.subtopic;
    for (const tag of new Set(a.reasons)) {
      const entry = byTag.get(tag) ?? { tag, questionIds: [], subtopics: [] };
      if (!entry.questionIds.includes(a.id)) entry.questionIds.push(a.id);
      if (subtopic && !entry.subtopics.includes(subtopic)) entry.subtopics.push(subtopic);
      byTag.set(tag, entry);
    }
  }

  return [...byTag.values()].sort((x, y) => y.questionIds.length - x.questionIds.length);
}

/** Reasons that showed up on more than one question. */
export function repeatedReasons(answers: DiagnosticAnswer[]): ReasonTally[] {
  return tallyReasons(answers).filter((t) => t.questionIds.length >= 2);
}

/**
 * The heading backs off when there is no repeat. Promising "your first
 * pattern" and then showing a bare score is worse than never promising
 * one, because the student notices.
 */
export function resultHeading(answers: DiagnosticAnswer[]): string {
  return repeatedReasons(answers).length
    ? "Your first Maren pattern"
    : "Your first Maren read";
}

/**
 * The sentence under the score. Returns null only when there is nothing
 * honest to say, so the caller can omit the block entirely rather than
 * render an empty box.
 */
export function findingLines(answers: DiagnosticAnswer[]): string[] {
  const tallies = tallyReasons(answers);
  if (!tallies.length) return [];

  const repeats = tallies.filter((t) => t.questionIds.length >= 2);

  if (repeats.length) {
    // Two is the cap. A third line on five questions is padding, and it
    // dilutes the one finding that actually earned its place.
    return repeats.slice(0, 2).map((t) => {
      const where = joinTopics(t.subtopics);
      return `${cap(REASON_PHRASE[t.tag])} came up on ${t.questionIds.length} of your questions${
        where ? `, in ${where}` : ""
      }.`;
    });
  }

  // No repeat. Say so plainly and report what was named, without
  // dressing a single occurrence up as a trend.
  return [
    `No single reason repeated across these five. You named ${listOf(
      tallies.map((t) => REASON_PHRASE[t.tag]),
      3,
    )}.`,
  ];
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
